import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import * as XLSX from 'xlsx';
import DonorDetail from './DonorDetail';
import useStore from '../useStore';

function LoadingIndicator() {
  return (
    <div className="loading-container">
      <div className="spinner"></div>
      <div className="loading-label">Loading</div>
    </div>
  );
}

function DonorList({ year, onBack }) {
  const [donors, setDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDonor, setSelectedDonor] = useState(null);
  const { selectedAgency } = useStore();

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/Donors?year=${year.fiscalYear}&desig=${selectedAgency?.vendorId}`)
      .then((response) => {
        const dataWithIds = response.data.map((donor, index) => ({
          ...donor,
          id: donor.id || index, // Ensure each allocation has a unique id
          donors: donor.donors || [] // Ensure payments is always an array
        }));
        setDonors(dataWithIds);
        setLoading(false);
      })
      .catch((error) => {
        if (error.response) {
          console.log('Response error:', error.response.data);
          setError(error.response.data);
        } else if (error.request) {
          console.log('Request error:', error.request);
          setError('No response received from server');
        } else {
          console.log('General error:', error.message);
          setError(error.message);
        }
        setLoading(false);
      });
  }, [year, selectedAgency]);

  if (loading) return <LoadingIndicator />;
  if (error) return <div>Error: {error.message}</div>;

  const columns = [
    { field: 'desigCode', headerName: 'Program Code', width: 150 },
    { field: 'programName', headerName: 'Program Name', width: 150 },
    { field: 'companyId', headerName: 'Company Id', width: 150 },
    { field: 'companyName', headerName: 'Company Name', width: 150 },
    {
      field: 'donorId', headerName: 'Donor Id', width: 150,
      renderCell: (params) => (
        <span style={{ color: 'red', cursor: 'pointer' }}>
          {params.value}
        </span>
      )
    },
    { field: 'donorName', headerName: 'Donor Name', width: 150 },
    { field: 'address', headerName: 'Address', width: 150 },
    { field: 'city', headerName: 'City', width: 150 },
    { field: 'state', headerName: 'State', width: 150 },
    { field: 'zip', headerName: 'Zip', width: 150 },
    {
      field: 'giftAmount', headerName: 'Gift Amount', width: 150, valueFormatter: (params) => {
        const v = parseFloat(params)
        return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
      }
    },
    { field: 'year', headerName: 'Year', width: 150 },
    { field: 'designationPlanId', headerName: 'Designated Gift Id', width: 150 }
  ];

  const downloadExcel = () => {
    const formattedData = donors.map(donor => {
      const formattedRow = {};
      columns.forEach(column => {
        formattedRow[column.headerName] = donor[column.field];
      });
      return formattedRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Donors");

    XLSX.writeFile(workbook, `Donors_${year.fiscalYear}.xlsx`);
  };

  const downloadCSV = () => {
    const csvData = donors.map(donor => {
      return columns.map(column => donor[column.field]).join(",");
    });

    const csvHeader = columns.map(column => column.headerName).join(",");
    const csvContent = [csvHeader, ...csvData].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `Donors_${year.fiscalYear}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRowClick = (params) => {
    setSelectedDonor(params.row);
  };

  const columnVisibilityModel = {
    allocationId: false, // Set this to false to hide the column
};

  return (
    <div>
      {selectedDonor ? (
        <DonorDetail allocation={selectedDonor} onBack={() => setSelectedDonor(null)} />
      ) : (
        <div style={{ height: 400, width: '100%' }}>
          <button className="gradient-button margin-right" onClick={onBack}>Back to Years</button>
          <button className="gradient-button margin-right" onClick={downloadExcel}>Download Excel</button>
          <button className="gradient-button" onClick={downloadCSV}>Download CSV</button>
          <h2>Donors for {year.fiscalYear}</h2>
          <DataGrid
            rows={donors}
            columns={columns}
            pageSize={5}
            onRowClick={handleRowClick}
            columnVisibilityModel={columnVisibilityModel}
          />
        </div>
      )}
    </div>
  );
}

export default DonorList;